/**
 * Book Ticker Test Page
 *
 * Tests if CCXT's watchBidsAsks works for Binance Futures.
 * This will verify whether the comment "watchBidsAsks is not supported by CCXT for Binance Futures" is correct or not.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';

interface BookTickerData {
  symbol: string;
  bid: number;
  bidVolume: number;
  ask: number;
  askVolume: number;
  timestamp: number;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

export const BookTickerTestPage: React.FC = () => {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [bookTickers, setBookTickers] = useState<Map<string, BookTickerData>>(new Map());
  const [updateCount, setUpdateCount] = useState(0);
  const [lastUpdateTime, setLastUpdateTime] = useState<number | null>(null);

  const exchangeRef = useRef<any>(null);
  const connectedRef = useRef(false);

  /**
   * Connect to Binance Futures and start watchBidsAsks
   */
  const connect = async () => {
    try {
      setConnectionState('connecting');
      setError(null);

      console.log('[BookTickerTest] Loading CCXT module...');

      // Dynamic import CCXT
      const ccxtModule = await import('ccxt');
      const ccxt = (ccxtModule.default ?? ccxtModule) as typeof import('ccxt');

      console.log('[BookTickerTest] Creating binanceusdm exchange...');

      // Create Binance USDⓈ-M Futures exchange
      exchangeRef.current = new ccxt.pro.binanceusdm({
        enableRateLimit: true,
      });

      connectedRef.current = true;
      setConnectionState('connected');

      console.log('[BookTickerTest] Starting watchBidsAsks...');

      // CCXT requires specific symbols for watchBidsAsks on binanceusdm
      // Using undefined returns error: "requires symbols for this channel"
      // We'll test with a few popular symbols first
      const testSymbols = [
        'BTC/USDT:USDT',
        'ETH/USDT:USDT',
        'BNB/USDT:USDT',
        'SOL/USDT:USDT',
        'XRP/USDT:USDT',
      ];

      console.log('[BookTickerTest] Testing with symbols:', testSymbols);

      // Start watching book tickers
      while (connectedRef.current) {
        try {
          // Try watchBidsAsks with specific symbols
          const tickers = await exchangeRef.current.watchBidsAsks(testSymbols);

          if (tickers) {
            setUpdateCount((prev) => prev + 1);
            setLastUpdateTime(Date.now());

            // Update state with received data
            setBookTickers((prev) => {
              const newMap = new Map(prev);

              for (const symbol in tickers) {
                const ticker = tickers[symbol];

                // Only process USDT perpetuals
                if (!ticker.symbol.includes(':USDT')) continue;

                // Skip delivery contracts
                const normalizedSymbol = ticker.symbol.replace(':USDT', '');
                if (normalizedSymbol.includes('-')) continue;

                newMap.set(normalizedSymbol, {
                  symbol: normalizedSymbol,
                  bid: ticker.bid || 0,
                  bidVolume: ticker.bidVolume || 0,
                  ask: ticker.ask || 0,
                  askVolume: ticker.askVolume || 0,
                  timestamp: ticker.timestamp || Date.now(),
                });
              }

              return newMap;
            });
          }
        } catch (err) {
          console.error('[BookTickerTest] watchBidsAsks error:', err);
          if (connectedRef.current) {
            setError(`watchBidsAsks error: ${err instanceof Error ? err.message : String(err)}`);
          }
          break;
        }
      }
    } catch (err) {
      console.error('[BookTickerTest] Connection error:', err);
      setError(`Connection error: ${err instanceof Error ? err.message : String(err)}`);
      setConnectionState('error');
    }
  };

  /**
   * Disconnect
   */
  const disconnect = async () => {
    console.log('[BookTickerTest] Disconnecting...');
    connectedRef.current = false;

    if (exchangeRef.current) {
      try {
        await exchangeRef.current.close();
      } catch (err) {
        console.error('[BookTickerTest] Disconnect error:', err);
      }
      exchangeRef.current = null;
    }

    setConnectionState('disconnected');
    setBookTickers(new Map());
    setUpdateCount(0);
    setLastUpdateTime(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      connectedRef.current = false;
      if (exchangeRef.current) {
        exchangeRef.current.close().catch(() => {});
      }
    };
  }, []);

  // Convert Map to sorted array for display
  const sortedTickers = Array.from(bookTickers.values())
    .sort((a, b) => a.symbol.localeCompare(b.symbol))
    .slice(0, 50); // Show first 50

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        CCXT watchBidsAsks Test (Binance Futures)
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Testing if CCXT's <code>watchBidsAsks()</code> works for Binance USDⓈ-M Futures.
        <br />
        This subscribes to the <code>!bookTicker</code> stream for best bid/ask prices.
      </Typography>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={connect}
            disabled={connectionState === 'connecting' || connectionState === 'connected'}
          >
            {connectionState === 'connecting' ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Connecting...
              </>
            ) : (
              'Connect & Test'
            )}
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={disconnect}
            disabled={connectionState === 'disconnected'}
          >
            Disconnect
          </Button>

          <Chip
            label={connectionState.toUpperCase()}
            color={
              connectionState === 'connected'
                ? 'success'
                : connectionState === 'error'
                ? 'error'
                : connectionState === 'connecting'
                ? 'warning'
                : 'default'
            }
          />
        </Box>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">Error:</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {/* Stats */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Statistics
        </Typography>
        <Box sx={{ display: 'flex', gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Symbols Received
            </Typography>
            <Typography variant="h5">{bookTickers.size}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Update Count
            </Typography>
            <Typography variant="h5">{updateCount}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Last Update
            </Typography>
            <Typography variant="h5">
              {lastUpdateTime ? new Date(lastUpdateTime).toLocaleTimeString() : '—'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Result */}
      {connectionState === 'connected' && bookTickers.size > 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">
            watchBidsAsks WORKS for Binance Futures!
          </Typography>
          <Typography variant="body2">
            Received {bookTickers.size} symbols with {updateCount} updates.
            The comment in the code is WRONG - CCXT does support watchBidsAsks for binanceusdm.
          </Typography>
        </Alert>
      )}

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Symbol</TableCell>
              <TableCell align="right">Best Bid</TableCell>
              <TableCell align="right">Bid Qty</TableCell>
              <TableCell align="right">Best Ask</TableCell>
              <TableCell align="right">Ask Qty</TableCell>
              <TableCell align="right">Spread</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTickers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {connectionState === 'connected'
                    ? 'Waiting for data...'
                    : 'Click "Connect & Test" to start'}
                </TableCell>
              </TableRow>
            ) : (
              sortedTickers.map((ticker) => {
                const spread = ticker.ask - ticker.bid;
                const spreadPercent = ticker.bid > 0 ? (spread / ticker.bid) * 100 : 0;

                return (
                  <TableRow key={ticker.symbol}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {ticker.symbol}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: 'success.main' }}>
                        {ticker.bid.toFixed(4)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {ticker.bidVolume.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: 'error.main' }}>
                        {ticker.ask.toFixed(4)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {ticker.askVolume.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {spreadPercent.toFixed(4)}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedTickers.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing first 50 of {bookTickers.size} symbols
        </Typography>
      )}
    </Container>
  );
};

export default BookTickerTestPage;
